

import React, { useState, useEffect } from 'react';
import {
  Box,
  Modal,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  CircularProgress,
} from '@mui/material';
import { Autocomplete } from '@mui/material'; // Add this import
import { Snackbar, Alert } from '@mui/material'; // Add this import
import MDTypography from 'components/MDTypography';
import MDButton from 'components/MDButton';
import MDBox from 'components/MDBox';
import { FormHelperText } from '@mui/material';
import { useAuth } from 'auth/Auth';
import colors from "assets/theme/base/colors";
import { toLocalDateTimeApi, formatLocalDateInput, formatLocalDateTimeInput, normalizeTimeToHHMMSS } from 'utils/DateTimeConversion';
import { REMINDER_OPTIONS, reminderAtFor } from 'layouts/callcenter/followUp';

const getDefaultDueDate = () => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  date.setHours(0, 0, 0, 0);
  return formatLocalDateTimeInput(date);
};

// Add this style object at the top of your component
const selectStyles = {
  height: '45px',
  '& .MuiOutlinedInput-root': {
    height: '45px',
  },
};

const EMPTY_ERRORS = {
  type: '',
  title: '',
  due_date: '',
  status: '',
  starttime: '',
  endtime: '',
};

const ActivityModal = ({ isOpen, onClose, onActivitySaved, parentId, parent, currentUser }) => {
  const { axiosInstance } = useAuth();
  const [newActivity, setNewActivity] = useState({
    type: '',
    title: '', // Add this
    status: 'Scheduled',
    description: '',
    due_date: getDefaultDueDate(),
    starttime: '',
    endtime: '',
    assigned_to: null,
  });
  // Reminder for the assignee (activities.reminder_at); 'none' = no reminder.
  const [reminder, setReminder] = useState('none');

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' // 'error', 'warning', 'info', 'success'
  });
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [validationErrors, setValidationErrors] = useState(EMPTY_ERRORS);

  useEffect(() => {
    if (isOpen && users.length === 0) fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (currentUser) {
      setNewActivity((prev) => ({ ...prev, assigned_to: currentUser.id }));
    }
  }, [currentUser]);

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await axiosInstance.get('/users/');
      if (response.data) {
        setUsers(response.data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoadingUsers(false);
    }
  };


  const handleInputChange = (event) => {
    const { name, value } = event.target;
    const updatedActivity = { ...newActivity, [name]: value };

    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: '',
        ...(name === 'type' ? { starttime: '', endtime: '' } : {}),
      }));
    }

    // Validate date and status when either changes
    if (name === 'due_date' || name === 'status') {
      validateDateStatus(
        name === 'due_date' ? value : newActivity.due_date,
        name === 'status' ? value : newActivity.status
      );
    }
  
    setNewActivity(updatedActivity);
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleSaveActivity = async () => {

    const dateOk = validateDateStatus(newActivity.due_date, newActivity.status);
    const fieldsOk = validateRequiredFields();

    if (!dateOk || !fieldsOk) {
      showSnackbar('Please correct the highlighted fields', 'error');
      return;
    }
  
    setIsLoading(true);
    try {
      const activityData = {
        title: newActivity.title, // Add this
        type: newActivity.type,
        description: newActivity.description,
        due_date: newActivity.type === 'Meeting'
            ? formatLocalDateInput(newActivity.due_date)
            : toLocalDateTimeApi(newActivity.due_date),
        parent_table: parent,
        parent_table_id: parentId,
        assigned_to: newActivity.assigned_to,
        status: newActivity.status,
        starttime: newActivity.type === 'Meeting' ? normalizeTimeToHHMMSS(newActivity.starttime) : null,
        endtime: newActivity.type === 'Meeting' ? normalizeTimeToHHMMSS(newActivity.endtime) : null,
        // A meeting is reminded relative to its start time.
        reminder_at: newActivity.status === 'Scheduled'
          ? reminderAtFor(
              newActivity.type === 'Meeting'
                ? `${String(newActivity.due_date).split('T')[0]}T${(newActivity.starttime || '09:00').slice(0, 5)}`
                : newActivity.due_date,
              reminder
            )
          : null,
      };

      const response = await axiosInstance.post('/activities/', activityData);

      showSnackbar('Activity saved successfully');
      onActivitySaved(response.data);
      handleClose();
    } catch (error) {
      console.error('Error saving activity:', error);
      showSnackbar(error.response?.data?.detail || 'Failed to save activity', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setNewActivity({
      type: '',
      title: '',
      status: 'Scheduled',
      description: '',
      due_date: getDefaultDueDate(),
      starttime: '',
      endtime: '',
      assigned_to: currentUser?.id ?? null,
    });
    setReminder('none');
    setValidationErrors(EMPTY_ERRORS);
    onClose();
  };

// Modify the handleStartTimeChange function
const handleStartTimeChange = (event) => {
  const startTime = event.target.value;
  
  // Update start time in state
  setNewActivity(prev => ({
    ...prev,
    starttime: startTime
  }));

  // Auto-populate end time (1 hour later)
  if (startTime) {
    const [hours, minutes] = startTime.split(':');
    let endHours = parseInt(hours) + 1;
    if (endHours > 23) endHours = 23;
    const endTime = `${endHours.toString().padStart(2, '0')}:${minutes}`;
    
    setNewActivity(prev => ({
      ...prev,
      endtime: endTime
    }));
  }
};

  const validateDateStatus = (date, status) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(date);
    selectedDate.setHours(0, 0, 0, 0);

    let errors = { due_date: '', status: '' };

    if (!date) {
      errors.due_date = 'Due date is required';
    } else if (status === 'Scheduled' && selectedDate < today) {
      errors.due_date = 'Cannot set past date for scheduled activities';
      errors.status = 'Status cannot be scheduled for past dates';
    }

    setValidationErrors((prev) => ({ ...prev, ...errors }));   // was: setValidationErrors(errors)
    return !errors.due_date && !errors.status;
  };

  const validateRequiredFields = () => {
    const errors = { type: '', title: '', starttime: '', endtime: '' };

    if (!newActivity.type) errors.type = 'Activity type is required';
    if (!newActivity.title.trim()) errors.title = 'Title is required';

    if (newActivity.type === 'Meeting') {
      const start = (newActivity.starttime || '').slice(0, 5);
      const end = (newActivity.endtime || '').slice(0, 5);

      if (!start) errors.starttime = 'Start time is required';
      if (!end) errors.endtime = 'End time is required';
      if (start && end && end <= start) errors.endtime = 'End time must be after start time';
    }

    setValidationErrors((prev) => ({ ...prev, ...errors }));
    return !Object.values(errors).some(Boolean);
  };

  return (
    <>
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="activity-modal-title"
    >
      <Box sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '92vw', sm: 400 },
        maxWidth: 400,
        maxHeight: '90vh',
        bgcolor: 'background.paper',
        boxShadow: 24,
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        {/* Header - Fixed position */}
        <Box className="pearl-popup-header"
          sx={{
            backgroundColor: colors.popupHeader.background,
            borderTopLeftRadius: 8,
            borderTopRightRadius: 8,
            px: 4,
            py: 3,
            flexShrink: 0,
          }}
        >
          <MDTypography 
            id="activity-modal-title" 
            variant="h6" 
            component="h2"
            sx={{ color: '#FFFFFF' }}
          >
            Add New Activity
          </MDTypography>
        </Box>

        {/* Scrollable content area */}
        <Box 
          sx={{ 
            overflowY: 'auto',
            flex: '1 1 auto',
            minHeight: 0,
            p: 4,
            '&::-webkit-scrollbar': {
              width: '0.4em'
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: '#888',
              borderRadius: '4px'
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#555'
            }
          }}
        >
          <FormControl fullWidth margin="normal" error={!!validationErrors.type}>
            <InputLabel id="activity-type-label">Activity Type</InputLabel>
            <Select
              labelId="activity-type-label"
              id="activity-type"
              name="type"
              value={newActivity.type}
              label="Activity Type"
              onChange={handleInputChange}
              sx={selectStyles}
            >
              <MenuItem value="Call">Call</MenuItem>
              <MenuItem value="Meeting">Meeting</MenuItem>
              <MenuItem value="Email">Email</MenuItem>
              <MenuItem value="Task">Task</MenuItem>
            </Select>
            {validationErrors.type && <FormHelperText>{validationErrors.type}</FormHelperText>}
          </FormControl>

          {/* Add the new Title field here */}
          <TextField
            fullWidth
            required
            label="Title"
            name="title"
            value={newActivity.title}
            onChange={handleInputChange}
            margin="normal"
            error={!!validationErrors.title}
            helperText={validationErrors.title}
            sx={{
              '& .MuiOutlinedInput-root': {
                height: '45px',
              }
            }}
          />

          <TextField
            fullWidth
            label="Description"
            name="description"
            value={newActivity.description}
            onChange={handleInputChange}
            margin="normal"
            multiline
            rows={4}
          />

          <TextField
          fullWidth
          label="Due Date"
          name="due_date"
          type={newActivity.type === 'Meeting' ? "date" : "datetime-local"}
          value={newActivity.type === 'Meeting' 
            ? newActivity.due_date.split('T')[0] 
            : newActivity.due_date}
          onChange={handleInputChange}
          margin="normal"
          InputLabelProps={{
            shrink: true,
          }}
          error={!!validationErrors.due_date}
          helperText={validationErrors.due_date}
        />

      {newActivity.type === 'Meeting' && (
          <>
            <TextField
              fullWidth
              label="Start Time"
              name="starttime"
              type="time"
              value={newActivity.starttime || ''} // Add fallback empty string
              onChange={handleStartTimeChange}
              margin="normal"
              error={!!validationErrors.starttime}
              helperText={validationErrors.starttime}
              required
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                step: 300
              }}
            />
            <TextField
              fullWidth
              label="End Time"
              name="endtime"
              type="time"
              value={newActivity.endtime || ''} // Add fallback empty string
              onChange={handleInputChange}
              margin="normal"
              error={!!validationErrors.endtime}
              helperText={validationErrors.endtime}
              required
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                step: 300
              }}
            />
          </>
        )}

      <FormControl fullWidth margin="normal" error={!!validationErrors.status}>
        <InputLabel id="activity-status-label">Activity Status</InputLabel>
        <Select
          labelId="activity-status-label"
          id="activity-status"
          name="status"
          value={newActivity.status}
          label="Activity Status"
          onChange={handleInputChange}
          sx={selectStyles}
        >
          <MenuItem value="Scheduled">Scheduled</MenuItem>
          <MenuItem value="Completed">Completed</MenuItem>
          <MenuItem value="Cancelled">Cancelled</MenuItem>
        </Select>
        {validationErrors.status && (
          <FormHelperText>{validationErrors.status}</FormHelperText>
        )}
      </FormControl>

      {newActivity.status === 'Scheduled' && (
        <FormControl fullWidth margin="normal">
          <InputLabel id="activity-reminder-label">Remind</InputLabel>
          <Select
            labelId="activity-reminder-label"
            id="activity-reminder"
            value={reminder}
            label="Remind"
            onChange={(e) => setReminder(e.target.value)}
            sx={selectStyles}
          >
            {REMINDER_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </Select>
          <FormHelperText>Whoever it is assigned to gets a notification at that time.</FormHelperText>
        </FormControl>
      )}

      {/* Update the Assigned To FormControl section: */}
      <Autocomplete
          fullWidth
          options={users}
          getOptionLabel={(option) => option.fullname || ''}
          value={users.find(user => user.id === newActivity.assigned_to) || null}
          loading={isLoadingUsers}
          onChange={(event, newValue) => {
            handleInputChange({
              target: {
                name: 'assigned_to',
                value: newValue?.id || null
              }
            });
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Assigned To"
              margin="normal"  // Add this
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {isLoadingUsers ? <CircularProgress size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          sx={{ 
            mb: 3,  // Increase margin bottom
            '& .MuiOutlinedInput-root': {
              height: '45px',
            }
          }}
        />

        </Box>

        {/* Sticky footer actions */}
        <MDBox
          px={4}
          py={2}
          display="flex"
          justifyContent="flex-end"
          sx={{
            flexShrink: 0,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <MDButton onClick={handleClose} color="secondary" sx={{ marginRight: 2 }}>
            Cancel
          </MDButton>
          <MDButton 
            onClick={handleSaveActivity} 
            variant="gradient" 
            color="info" 
            disabled={isLoading}
          >
            {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Save'}
          </MDButton>
        </MDBox>
      </Box>
    </Modal>

    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
    >
      <Alert 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
        severity={snackbar.severity}
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
    </>
  );
};

export default ActivityModal;